import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, FileText, PenTool, Eye, Pencil, Repeat, AlertTriangle, FileEdit, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { authenticatedDownload } from "../lib/pdf";
import { PERMITTED_USE_OPTIONS, US_STATES } from "../constants/leaseOptions";
import { formatUSD, parseUSD } from "../lib/currency";
import LeaseAgreementEditorDialog from "../components/LeaseAgreementEditorDialog";
import CounterOfferInbox from "../components/CounterOfferInbox";
import RenewalInsights from "../components/RenewalInsights";
import USDateInput from "../components/USDateInput";

export default function Leases() {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [esignAccount, setEsignAccount] = useState({ configured: false });
  const [envelopesByLease, setEnvelopesByLease] = useState({});
  const [sendingId, setSendingId] = useState(null);
  const [renewing, setRenewing] = useState(null);
  const [renewForm, setRenewForm] = useState({ start_date: "", end_date: "", monthly_rent: "", deposit: "", landlord_message: "", send_to_tenant: false });
  const [renewSubmitting, setRenewSubmitting] = useState(false);
  const [expiring, setExpiring] = useState([]);
  const [editingAgreement, setEditingAgreement] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  function defaultForm() {
    return { property_id: "", tenant_id: "", unit_label: "Unit 1", monthly_rent: 0, start_date: "", end_date: "", deposit: 0, lease_type: "general", lease_category: "commercial", permitted_use: "", governing_state: "" };
  }

  const LEASE_TYPE_OPTIONS = [
    { value: "general", label: "General" },
    { value: "gross", label: "Gross (landlord pays opex)" },
    { value: "triple_net", label: "Triple Net / NNN (tenant pays opex)" },
  ];
  const LEASE_TYPE_LABELS = {
    general: "General",
    gross: "Gross",
    triple_net: "Triple Net (NNN)",
  };
  const leaseTypeLabel = (value) => LEASE_TYPE_LABELS[value] || "General";

  const load = async () => {
    const [l, p, t, esign, exp] = await Promise.all([
      api.get("/leases"), api.get("/properties"), api.get("/tenants"),
      api.get("/esignature/account").catch(() => ({ data: { configured: false } })),
      api.get("/leases/expiring/upcoming").catch(() => ({ data: [] })),
    ]);
    setItems(l.data); setProperties(p.data); setTenants(t.data);
    setEsignAccount(esign.data || { configured: false });
    setExpiring(Array.isArray(exp.data) ? exp.data : []);
    // Pull latest envelope per lease in parallel
    const pairs = await Promise.all(
      (l.data || []).map((lease) =>
        api.get(`/leases/${lease.id}/esignature/envelopes`).then(
          (r) => [lease.id, (r.data || [])[0] || null],
          () => [lease.id, null],
        ),
      ),
    );
    setEnvelopesByLease(Object.fromEntries(pairs));
    return l.data || [];
  };
  useEffect(() => { load(); }, []);

  // Handle `?renew=<lease_id>` deep-link from expiry-alert email — auto-open
  // the renewal dialog once the lease list has loaded.
  useEffect(() => {
    const renewId = searchParams.get("renew");
    if (!renewId || !items.length) return;
    const target = items.find((l) => l.id === renewId);
    if (target && !target.renewed_to_lease_id) {
      openRenew(target);
    }
    // Strip the param from the URL once handled
    const next = new URLSearchParams(searchParams);
    next.delete("renew");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      monthly_rent: Number(form.monthly_rent),
      deposit: Number(form.deposit),
      permitted_use: form.permitted_use ? form.permitted_use : null,
      governing_state: form.governing_state ? form.governing_state : null,
    };
    try {
      if (editingId) {
        await api.put(`/leases/${editingId}`, payload);
        toast.success("Lease updated");
      } else {
        await api.post("/leases", payload);
        toast.success("Lease created");
      }
      setOpen(false);
      setEditingId(null);
      setForm(defaultForm());
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm());
    setOpen(true);
  };

  const openEdit = (lease) => {
    setEditingId(lease.id);
    setForm({
      property_id: lease.property_id || "",
      tenant_id: lease.tenant_id || "",
      unit_label: lease.unit_label || "",
      monthly_rent: lease.monthly_rent ?? 0,
      start_date: lease.start_date || "",
      end_date: lease.end_date || "",
      deposit: lease.deposit ?? 0,
      lease_type: lease.lease_type || "general",
      lease_category: lease.lease_category || "commercial",
      permitted_use: lease.permitted_use || "",
      governing_state: lease.governing_state || "",
    });
    setOpen(true);
  };

  const remove = async (lease) => {
    if (!confirm(`Delete the lease for ${lease.tenant_name || "this tenant"} at ${lease.property_name || "this property"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/leases/${lease.id}`);
      toast.success("Lease deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete lease");
    }
  };

  const downloadAgreement = async (lease) => {
    try {
      toast.loading("Generating lease draft PDF…", { id: `lease-pdf-${lease.id}` });
      const safeProp = (lease.property_name || "property").replace(/[^A-Za-z0-9_-]+/g, "_");
      const safeTenant = (lease.tenant_name || "tenant").replace(/[^A-Za-z0-9_-]+/g, "_");
      await authenticatedDownload(
        `/api/leases/${lease.id}/agreement/pdf`,
        `lease-draft-${safeProp}-${safeTenant}.pdf`,
      );
      toast.success("Lease draft downloaded", { id: `lease-pdf-${lease.id}` });
    } catch (e) {
      toast.error("Failed to generate lease PDF", { id: `lease-pdf-${lease.id}` });
    }
  };

  const downloadSignedPdf = async (lease) => {
    const envelope = envelopesByLease[lease.id];
    if (!envelope?.id) {
      toast.error("No signed envelope on file yet.");
      return;
    }
    try {
      toast.loading("Fetching signed PDF…", { id: `lease-signed-${lease.id}` });
      const safeProp = (lease.property_name || "property").replace(/[^A-Za-z0-9_-]+/g, "_");
      const safeTenant = (lease.tenant_name || "tenant").replace(/[^A-Za-z0-9_-]+/g, "_");
      await authenticatedDownload(
        `/api/esignature/envelopes/${envelope.id}/signed-pdf`,
        `lease-signed-${safeProp}-${safeTenant}.pdf`,
      );
      toast.success("Signed PDF downloaded", { id: `lease-signed-${lease.id}` });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not download signed PDF", { id: `lease-signed-${lease.id}` });
    }
  };

  const sendForSignature = async (lease) => {
    if (!esignAccount.configured) {
      toast.error("Connect Dropbox Sign or DocuSign in Settings first.");
      return;
    }
    if (!confirm(`Send the lease draft to ${lease.tenant_email || "the tenant"} for e-signature via ${esignAccount.provider === "docusign" ? "DocuSign" : "Dropbox Sign"}?`)) return;
    setSendingId(lease.id);
    try {
      const r = await api.post(`/leases/${lease.id}/esignature/send`, {});
      toast.success("Lease sent for e-signature");
      setEnvelopesByLease((prev) => ({ ...prev, [lease.id]: r.data }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send for signature");
    } finally {
      setSendingId(null);
    }
  };

  const openRenew = (lease) => {
    // Default start = old end + 1 day; default end = start + 12 months - 1 day
    let defaultStart = "";
    let defaultEnd = "";
    if (lease.end_date) {
      const oldEnd = new Date(`${lease.end_date}T00:00:00`);
      const start = new Date(oldEnd); start.setUTCDate(start.getUTCDate() + 1);
      const end = new Date(start); end.setUTCFullYear(end.getUTCFullYear() + 1); end.setUTCDate(end.getUTCDate() - 1);
      defaultStart = start.toISOString().slice(0, 10);
      defaultEnd = end.toISOString().slice(0, 10);
    }
    setRenewing(lease);
    setRenewForm({
      start_date: defaultStart,
      end_date: defaultEnd,
      monthly_rent: String(lease.monthly_rent ?? ""),
      deposit: String(lease.deposit ?? ""),
      landlord_message: "",
      send_to_tenant: false,
    });
  };

  const submitRenew = async (e) => {
    e.preventDefault();
    if (!renewing) return;
    setRenewSubmitting(true);
    const payload = {};
    if (renewForm.start_date) payload.start_date = renewForm.start_date;
    if (renewForm.end_date) payload.end_date = renewForm.end_date;
    if (renewForm.monthly_rent !== "" && renewForm.monthly_rent != null) payload.monthly_rent = Number(renewForm.monthly_rent);
    if (renewForm.deposit !== "" && renewForm.deposit != null) payload.deposit = Number(renewForm.deposit);
    try {
      if (renewForm.send_to_tenant) {
        // Tenant-facing proposal — landlord proposes, tenant accepts via tokenized URL
        const proposalPayload = {
          start_date: payload.start_date,
          end_date: payload.end_date,
          monthly_rent: payload.monthly_rent,
          deposit: payload.deposit ?? 0,
        };
        if (renewForm.landlord_message) proposalPayload.landlord_message = renewForm.landlord_message;
        await api.post(`/leases/${renewing.id}/renewal-proposal`, proposalPayload);
        toast.success(`Renewal offer emailed to ${renewing.tenant_email || "tenant"}`);
      } else {
        await api.post(`/leases/${renewing.id}/renew`, payload);
        toast.success("Lease renewed");
      }
      setRenewing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not renew lease");
    } finally {
      setRenewSubmitting(false);
    }
  };

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline text-slate-500">Contracts</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Leases</h1>
            <p className="text-sm text-slate-500 mt-1">{items.length} active lease contracts.</p>
          </div>
          <button data-testid="add-lease-button" onClick={openCreate} className="btn-primary-dark flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> Create Lease
          </button>
        </div>

        {expiring.length > 0 ? (
          <div
            className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4 flex items-start gap-3"
            data-testid="lease-expiring-banner"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-bold text-amber-900 mb-1">
                {expiring.length} lease{expiring.length === 1 ? "" : "s"} expiring within 90 days
              </div>
              <ul className="space-y-1">
                {expiring.slice(0, 5).map((lease) => (
                  <li key={lease.id} className="flex items-center justify-between gap-2 text-amber-900">
                    <span className="truncate">
                      <span className="font-semibold">{lease.tenant_name}</span>
                      <span className="text-amber-700"> · {lease.property_name} · {lease.unit_label}</span>
                      <span className="text-amber-600 font-mono ml-2">
                        ends {lease.end_date} · {lease.days_left} day{lease.days_left === 1 ? "" : "s"} left
                      </span>
                    </span>
                    <button
                      onClick={() => openRenew(lease)}
                      className="text-xs font-bold text-amber-900 underline hover:text-amber-700 shrink-0"
                      data-testid={`expiring-renew-${lease.id}`}
                    >
                      Renew →
                    </button>
                  </li>
                ))}
                {expiring.length > 5 ? (
                  <li className="text-amber-700 text-xs">…and {expiring.length - 5} more in the list below.</li>
                ) : null}
              </ul>
            </div>
          </div>
        ) : null}

        <RenewalInsights />
        <CounterOfferInbox onChanged={load} />

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="leases-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Property</Th><Th>Unit</Th><Th>Tenant</Th><Th>Period</Th>
                <Th className="text-right">Monthly Rent</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="text-center py-16 text-slate-400">No leases yet.</td></tr>}
              {items.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lease-row-${l.id}`}>
                  <Td className="font-semibold text-slate-900">{l.property_name}</Td>
                  <Td>
                    {l.unit_label}
                    <div className="mt-0.5">
                      <span
                        className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                          l.lease_type === "triple_net" ? "bg-violet-50 text-violet-800 border-violet-200" :
                          l.lease_type === "gross" ? "bg-sky-50 text-sky-800 border-sky-200" :
                          "bg-slate-100 text-slate-700 border-slate-200"
                        }`}
                        data-testid={`lease-type-badge-${l.id}`}
                      >
                        {leaseTypeLabel(l.lease_type)}
                      </span>
                    </div>
                  </Td>
                  <Td className="text-slate-600">{l.tenant_name}<div className="text-xs text-slate-400">{l.tenant_email}</div></Td>
                  <Td className="text-xs text-slate-500 font-mono">{l.start_date} → {l.end_date}</Td>
                  <Td className="text-right font-mono font-bold whitespace-nowrap">${l.monthly_rent.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                  <Td className="text-right">
                    <div className="inline-flex flex-wrap items-center justify-end gap-1">
                      <EnvelopeStatusBadge envelope={envelopesByLease[l.id]} />
                      <button
                        onClick={() => setViewing(l)}
                        className="text-slate-500 hover:text-slate-900 p-1 inline-flex items-center gap-1 text-xs font-semibold"
                        title="View lease details"
                        data-testid={`view-lease-${l.id}`}
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                      <button
                        onClick={() => openEdit(l)}
                        className="text-slate-500 hover:text-amber-600 p-1 inline-flex items-center gap-1 text-xs font-semibold"
                        title="Edit lease"
                        data-testid={`edit-lease-${l.id}`}
                      >
                        <Pencil className="w-4 h-4" /> Edit
                      </button>
                      {!l.renewed_to_lease_id ? (
                        <button
                          onClick={() => openRenew(l)}
                          className="text-slate-500 hover:text-emerald-700 p-1 inline-flex items-center gap-1 text-xs font-semibold"
                          title="Renew this lease — creates a new term continuing from the current end date"
                          data-testid={`renew-lease-${l.id}`}
                        >
                          <Repeat className="w-4 h-4" /> Renew
                        </button>
                      ) : (
                        <span
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-sm"
                          title="This lease has already been renewed"
                          data-testid={`renewed-badge-${l.id}`}
                        >
                          Renewed
                        </span>
                      )}
                      <button
                        onClick={() => sendForSignature(l)}
                        disabled={sendingId === l.id}
                        className={`p-1 inline-flex items-center gap-1 text-xs font-semibold ${esignAccount.configured ? "text-slate-600 hover:text-indigo-700" : "text-slate-300 cursor-not-allowed"}`}
                        title={esignAccount.configured ? "Send draft to tenant for e-signature" : "Connect Dropbox Sign or DocuSign in Settings first"}
                        data-testid={`send-esignature-${l.id}`}
                      >
                        <PenTool className="w-4 h-4" /> {sendingId === l.id ? "Sending…" : "E-sign"}
                      </button>
                      {envelopesByLease[l.id]?.signed_pdf_available ? (
                        <button
                          onClick={() => downloadSignedPdf(l)}
                          className="text-emerald-700 hover:text-emerald-900 p-1 inline-flex items-center gap-1 text-xs font-semibold"
                          title="Download fully executed (signed) PDF"
                          data-testid={`download-signed-pdf-${l.id}`}
                        >
                          <ShieldCheck className="w-4 h-4" /> Signed PDF
                        </button>
                      ) : null}
                      <button
                        onClick={() => setEditingAgreement(l)}
                        className="text-slate-500 hover:text-violet-700 p-1 inline-flex items-center gap-1 text-xs font-semibold"
                        title="Edit lease agreement — override clauses or upload a custom PDF"
                        data-testid={`edit-lease-agreement-${l.id}`}
                      >
                        <FileEdit className="w-4 h-4" /> Edit Agreement
                      </button>
                      <button
                        onClick={() => downloadAgreement(l)}
                        className="text-slate-500 hover:text-indigo-600 p-1 inline-flex items-center gap-1 text-xs font-semibold"
                        title="Download Modified Gross commercial lease draft (PDF)"
                        data-testid={`download-lease-agreement-${l.id}`}
                      >
                        <FileText className="w-4 h-4" /> Lease PDF
                      </button>
                      <button
                        onClick={() => remove(l)}
                        className="text-slate-500 hover:text-red-600 p-1"
                        title="Delete lease"
                        data-testid={`delete-lease-${l.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(defaultForm()); } }}>
          <DialogContent>
            <DialogHeader><DialogTitle style={{fontFamily:"Outfit"}}>{editingId ? "Edit lease" : "Create lease"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Property">
                  <select required className="input-clean w-full" data-testid="lease-property-select" value={form.property_id} onChange={(e)=>setForm({...form, property_id:e.target.value})}>
                    <option value="">Select property</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                <Field label="Tenant">
                  <select required className="input-clean w-full" data-testid="lease-tenant-select" value={form.tenant_id} onChange={(e)=>setForm({...form, tenant_id:e.target.value})}>
                    <option value="">Select tenant</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Lease category">
                  <select
                    required
                    className="input-clean w-full"
                    data-testid="lease-category-select"
                    value={form.lease_category}
                    onChange={(e) => setForm({ ...form, lease_category: e.target.value })}
                  >
                    <option value="commercial">Commercial</option>
                    <option value="residential">Residential</option>
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                    {form.lease_category === "residential"
                      ? "Residential leases are drafted by AI per state to comply with statutory disclosures."
                      : "Commercial leases use the static state-aware template."}
                  </p>
                </Field>
                <Field label={form.lease_category === "residential" ? "Term type" : "Lease type"}>
                  <select
                    required
                    className="input-clean w-full"
                    data-testid="lease-type-select"
                    value={form.lease_type}
                    onChange={(e) => setForm({ ...form, lease_type: e.target.value })}
                  >
                    {LEASE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                    {form.lease_category === "residential" ? "Residential expense allocation is set by state statute." :
                     form.lease_type === "gross" ? "Tenant pays rent only; landlord covers all operating expenses." :
                     form.lease_type === "triple_net" ? "Tenant pays rent + property taxes + insurance + CAM (NNN)." :
                     "Generic catch-all — operating expenses negotiated separately."}
                  </p>
                </Field>
              </div>
              <Field label="Unit label"><input required className="input-clean w-full" data-testid="lease-unit-input" value={form.unit_label} onChange={(e)=>setForm({...form, unit_label:e.target.value})} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Permitted use">
                  <select
                    className="input-clean w-full"
                    data-testid="lease-permitted-use-select"
                    value={form.permitted_use}
                    onChange={(e) => setForm({ ...form, permitted_use: e.target.value })}
                  >
                    <option value="">— Select (optional) —</option>
                    {PERMITTED_USE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Governing state">
                  <select
                    className="input-clean w-full"
                    data-testid="lease-governing-state-select"
                    value={form.governing_state}
                    onChange={(e) => setForm({ ...form, governing_state: e.target.value })}
                  >
                    <option value="">— Auto-detect from address —</option>
                    {US_STATES.map((s) => (
                      <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Monthly rent"><input required type="text" inputMode="decimal" className="input-clean w-full font-mono" data-testid="lease-rent-input" value={formatUSD(form.monthly_rent)} onChange={(e)=>setForm({...form, monthly_rent:parseUSD(e.target.value)})} placeholder="$0" /></Field>
                <Field label="Deposit"><input type="text" inputMode="decimal" className="input-clean w-full font-mono" data-testid="lease-deposit-input" value={formatUSD(form.deposit)} onChange={(e)=>setForm({...form, deposit:parseUSD(e.target.value)})} placeholder="$0" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start date"><USDateInput required data-testid="lease-start-input" value={form.start_date} onChange={(iso)=>setForm({...form, start_date:iso})} /></Field>
                <Field label="End date"><USDateInput required data-testid="lease-end-input" value={form.end_date} onChange={(iso)=>setForm({...form, end_date:iso})} /></Field>
              </div>
              <DialogFooter>
                <button type="button" onClick={()=>{ setOpen(false); setEditingId(null); setForm(defaultForm()); }} className="btn-outline-dark">Cancel</button>
                <button type="submit" className="btn-primary-dark" data-testid="lease-submit-button">{editingId ? "Save changes" : "Create"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle style={{fontFamily:"Outfit"}}>Lease details</DialogTitle></DialogHeader>
            {viewing ? <LeaseDetails lease={viewing} /> : null}
            <DialogFooter>
              <button type="button" onClick={() => setViewing(null)} className="btn-outline-dark" data-testid="view-lease-close">Close</button>
              <button
                type="button"
                onClick={() => { const l = viewing; setViewing(null); openEdit(l); }}
                className="btn-primary-dark inline-flex items-center gap-2"
                data-testid="view-lease-edit"
              >
                <Pencil className="w-4 h-4" /> Edit
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!renewing} onOpenChange={(o) => { if (!o) setRenewing(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{fontFamily:"Outfit"}}>Renew lease</DialogTitle>
            </DialogHeader>
            {renewing ? (
              <form onSubmit={submitRenew} className="space-y-4 text-sm" data-testid="renew-lease-form">
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-600">
                  Renewing the lease for <span className="font-semibold text-slate-900">{renewing.tenant_name}</span> at <span className="font-semibold text-slate-900">{renewing.property_name} · {renewing.unit_label}</span>. The current term ends <span className="font-mono">{renewing.end_date}</span>.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="New start date">
                    <USDateInput
                      required
                      value={renewForm.start_date}
                      onChange={(iso) => setRenewForm({ ...renewForm, start_date: iso })}
                      data-testid="renew-start-date"
                    />
                  </Field>
                  <Field label="New end date">
                    <USDateInput
                      required
                      value={renewForm.end_date}
                      onChange={(iso) => setRenewForm({ ...renewForm, end_date: iso })}
                      data-testid="renew-end-date"
                    />
                  </Field>
                  <Field label="Monthly rent ($)">
                    <input
                      type="number"
                      step="0.01"
                      required
                      className="input-clean w-full"
                      value={renewForm.monthly_rent}
                      onChange={(e) => setRenewForm({ ...renewForm, monthly_rent: e.target.value })}
                      data-testid="renew-monthly-rent"
                    />
                  </Field>
                  <Field label="Deposit ($)">
                    <input
                      type="number"
                      step="0.01"
                      className="input-clean w-full"
                      value={renewForm.deposit}
                      onChange={(e) => setRenewForm({ ...renewForm, deposit: e.target.value })}
                      data-testid="renew-deposit"
                    />
                  </Field>
                </div>
                <div className="text-[11px] text-slate-500">
                  The original lease will be marked <span className="font-mono">renewed</span> and linked to the new term. Property, tenant, and unit are inherited automatically.
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={renewForm.send_to_tenant}
                      onChange={(e) => setRenewForm({ ...renewForm, send_to_tenant: e.target.checked })}
                      data-testid="renew-send-to-tenant"
                      className="mt-1"
                    />
                    <span>
                      <span className="font-semibold text-slate-900">Send to tenant for approval</span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        Emails the tenant a tokenized link to accept or decline. The renewal only takes effect after they accept — nothing changes on this lease until then.
                      </span>
                    </span>
                  </label>
                  {renewForm.send_to_tenant ? (
                    <Field label="Optional message to tenant">
                      <textarea
                        rows={3}
                        maxLength={1000}
                        className="input-clean w-full"
                        placeholder="e.g. We'd love to keep you another year. Modest rent increase reflects market — happy to discuss."
                        value={renewForm.landlord_message}
                        onChange={(e) => setRenewForm({ ...renewForm, landlord_message: e.target.value })}
                        data-testid="renew-landlord-message"
                      />
                    </Field>
                  ) : null}
                </div>

                <DialogFooter>
                  <button type="button" onClick={() => setRenewing(null)} className="btn-outline-dark">Cancel</button>
                  <button
                    type="submit"
                    className="btn-primary-dark inline-flex items-center gap-2"
                    disabled={renewSubmitting}
                    data-testid="renew-submit-button"
                  >
                    <Repeat className="w-4 h-4" />
                    {renewSubmitting
                      ? (renewForm.send_to_tenant ? "Sending offer…" : "Renewing…")
                      : (renewForm.send_to_tenant ? "Send offer to tenant" : "Renew lease")}
                  </button>
                </DialogFooter>
              </form>
            ) : null}
          </DialogContent>
        </Dialog>

        <LeaseAgreementEditorDialog
          lease={editingAgreement}
          onClose={() => setEditingAgreement(null)}
          onUpdated={load}
        />
      </div>
    </DashboardShell>
  );
}

function LeaseDetails({ lease }) {
  const monthly = Number(lease.monthly_rent || 0);
  const deposit = Number(lease.deposit || 0);
  const leaseTypeMap = { general: "General", gross: "Gross", triple_net: "Triple Net (NNN)" };
  const leaseTypeLabel = leaseTypeMap[lease.lease_type] || "General";
  return (
    <div className="space-y-4 text-sm" data-testid="lease-details">
      <div className="grid grid-cols-2 gap-4">
        <Detail label="Property" value={lease.property_name || "—"} />
        <Detail label="Unit" value={lease.unit_label || "—"} />
        <Detail label="Tenant" value={lease.tenant_name || "—"} sub={lease.tenant_email} />
        <Detail label="Status" value={lease.status || "—"} />
        <Detail label="Lease type" value={leaseTypeLabel} testid="lease-type-detail" />
        <Detail label="Permitted use" value={lease.permitted_use || "—"} testid="lease-permitted-use-detail" />
        <Detail label="Governing state" value={lease.governing_state || "Auto-detect"} testid="lease-governing-state-detail" mono={!!lease.governing_state} />
        <Detail label="Start date" value={lease.start_date || "—"} mono />
        <Detail label="End date" value={lease.end_date || "—"} mono />
        <Detail label="Monthly rent" value={`$${monthly.toLocaleString()}`} mono />
        <Detail label="Deposit" value={`$${deposit.toLocaleString()}`} mono />
      </div>
      {lease.created_at ? (
        <div className="border-t border-slate-100 pt-3 text-xs text-slate-500 font-mono">
          Created {new Date(lease.created_at).toLocaleString()}{lease.updated_at ? ` · Updated ${new Date(lease.updated_at).toLocaleString()}` : ""}
        </div>
      ) : null}
      <LeaseActivityTimeline leaseId={lease.id} />
    </div>
  );
}

const _ACTIVITY_FIELD_LABELS = {
  property_id: "Property",
  tenant_id: "Tenant",
  unit_label: "Unit",
  monthly_rent: "Monthly rent",
  deposit: "Deposit",
  start_date: "Start date",
  end_date: "End date",
  status: "Status",
};

function _formatActivityValue(field, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (field === "monthly_rent" || field === "deposit") {
    const n = Number(value);
    return Number.isFinite(n) ? `$${n.toLocaleString()}` : String(value);
  }
  return String(value);
}

function LeaseActivityTimeline({ leaseId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/leases/${leaseId}/activity`).then(
      (r) => { if (!cancelled) setRows(r.data || []); },
      () => { if (!cancelled) setRows([]); },
    );
    return () => { cancelled = true; };
  }, [leaseId]);

  return (
    <details className="border-t border-slate-100 pt-3 group" data-testid="lease-activity-timeline">
      <summary className="cursor-pointer overline text-slate-600 hover:text-slate-900 inline-flex items-center gap-2 select-none">
        Activity history{rows ? ` (${rows.length})` : ""}
      </summary>
      <div className="mt-3 space-y-3">
        {rows === null ? (
          <div className="text-xs text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-slate-400">No activity recorded yet.</div>
        ) : (
          <ol className="relative border-l border-slate-200 pl-4 space-y-3">
            {rows.map((entry) => (
              <li key={entry.id} className="relative" data-testid={`lease-activity-row-${entry.id}`}>
                <span
                  className={`absolute -left-[19px] top-1 w-3 h-3 rounded-full border-2 border-white ring-1 ${
                    entry.action === "created" ? "bg-emerald-500 ring-emerald-200"
                    : entry.action === "deleted" ? "bg-rose-500 ring-rose-200"
                    : "bg-amber-500 ring-amber-200"
                  }`}
                />
                <div className="text-xs">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-bold text-slate-900 capitalize">{entry.action}</span>
                    <span className="text-slate-500">
                      by {entry.actor_email || entry.actor_name || "system"}
                    </span>
                    <span className="text-slate-400 font-mono">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ""}
                    </span>
                  </div>
                  {Array.isArray(entry.changes) && entry.changes.length > 0 ? (
                    <ul className="mt-1 space-y-0.5 text-slate-600">
                      {entry.changes.map((c) => (
                        <li key={`${entry.id}-${c.field}`} className="font-mono">
                          <span className="text-slate-500">{_ACTIVITY_FIELD_LABELS[c.field] || c.field}:</span>{" "}
                          <span className="text-rose-600">{_formatActivityValue(c.field, c.before)}</span>
                          <span className="text-slate-400"> → </span>
                          <span className="text-emerald-700">{_formatActivityValue(c.field, c.after)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </details>
  );
}

function Detail({ label, value, sub, mono, testid }) {
  return (
    <div>
      <div className="overline text-slate-500 mb-1">{label}</div>
      <div className={`font-semibold text-slate-900 ${mono ? "font-mono" : ""}`} data-testid={testid}>{value}</div>
      {sub ? <div className="text-xs text-slate-400">{sub}</div> : null}
    </div>
  );
}

function Th({ children, className="" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className="" }) { return <td className={`px-5 py-4 ${className}`}>{children}</td>; }
function Field({ label, children }) { return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>; }

function EnvelopeStatusBadge({ envelope }) {
  if (!envelope) return null;
  const tone = {
    completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    signed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    delivered: "bg-sky-50 text-sky-700 border-sky-200",
    viewed: "bg-sky-50 text-sky-700 border-sky-200",
    sent: "bg-slate-100 text-slate-700 border-slate-200",
    queued: "bg-slate-100 text-slate-700 border-slate-200",
    declined: "bg-rose-100 text-rose-800 border-rose-200",
    voided: "bg-rose-100 text-rose-800 border-rose-200",
    expired: "bg-amber-100 text-amber-800 border-amber-200",
  }[envelope.status] || "bg-slate-100 text-slate-700 border-slate-200";
  const provider = envelope.provider === "docusign" ? "DocuSign" : "Dropbox Sign";
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${tone} mr-1`}
      title={`${provider} · ${envelope.status}`}
      data-testid={`envelope-status-${envelope.lease_id}`}
    >
      {envelope.status}
    </span>
  );
}
